import { useMemo } from "react";
import classNames from "classnames";
import { useFormikContext } from "formik";
import type { OrganizationCloseStage } from "@common/types/organizationCloseStage";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { DescriptionListItem } from "@design-system/DescriptionList/DescriptionList";
import taskStyles from "@components/drawers/TaskDrawerFields.module.scss";
import { useCloseStageSelectFooter } from "../CloseStageSelectFooter";
import { NO_STAGE_VALUE } from "../closeTemplateTasksDrawer.constants";
import type { TemplateTaskDetailsValues } from "./templateTaskDetails.schema";

interface StageFieldProps {
    sortedStages: OrganizationCloseStage[];
}

export const StageField = ({ sortedStages }: StageFieldProps) => {
    const formik = useFormikContext<TemplateTaskDetailsValues>();
    const renderStageFooter = useCloseStageSelectFooter();

    const stageItems = useMemo<Array<PickerItemData<string>>>(
        () => [
            { value: NO_STAGE_VALUE, label: "No stage" },
            ...sortedStages.map((stage) => ({
                value: stage.id,
                label: stage.name,
            })),
        ],
        [sortedStages],
    );

    const selectedStageLabel = !formik.values.closeStageId
        ? "No stage"
        : (sortedStages.find((stage) => stage.id === formik.values.closeStageId)
              ?.name ?? "No stage");

    return (
        <DescriptionListItem label="Stage">
            <div className={taskStyles.detailSelectWrapper}>
                <Picker.Select
                    items={stageItems}
                    value={formik.values.closeStageId ?? NO_STAGE_VALUE}
                    onValueChange={(value) => {
                        void formik.setFieldValue(
                            "closeStageId",
                            !value || value === NO_STAGE_VALUE ? null : value,
                        );
                    }}
                    renderValue={() => selectedStageLabel}
                    renderFooter={renderStageFooter}
                    trigger={
                        <Picker.Button
                            variant="primary"
                            className={classNames(
                                taskStyles.detailSelectTrigger,
                                {
                                    [taskStyles.emptyValue]:
                                        !formik.values.closeStageId,
                                },
                            )}
                        />
                    }
                />
            </div>
        </DescriptionListItem>
    );
};
